import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Building2,
  UserCircle,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Lock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Shield,
  CheckCircle2,
  Info
} from 'lucide-react';

import { WorkerForm } from '../../types/worker';
import { REGISTRATION_STEPS, CONTRACT_TYPES, FRENCH_REGIONS, DRIVER_LICENSES } from '../../constants/worker';
import { validateStep } from '../../utils/validations';
import { createAuthAccount, createWorkerProfile, APIError } from '../../services/api_worker';
import { cn } from '../../lib/utils';
import { FormInput } from '../ui/FormInput';
import { FormSelect } from '../ui/FormSelect';
import { PasswordInput } from '../ui/PasswordInput';
import { ContractChecklist } from '../ui/ContractChecklist';

const initialFormState: WorkerForm = {
  first_name: '',
  last_name: '',
  email: '',
  job: '',
  contracts: [],
  area: '',
  phone: '',
  other_info: '',
  diploma: '',
  driver_license: '',
  caces: '',
  caces_type: '',
  cv_classic: null,
  password: '',
  confirm_password: '',
  terms_accepted: false
};

export function WorkerRegister() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WorkerForm>(initialFormState);

  const updateForm = (updates: Partial<WorkerForm>) => {
    setForm(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📝 Creating worker profile:', form);

      // Create auth account
      const authData = await createAuthAccount(form);
      console.log('✅ Auth account created:', authData);

      // Create worker profile
      const workerData = await createWorkerProfile(form, authData);
      console.log('✅ Worker profile created:', workerData);

      // Login and redirect
      login(authData.token, {
        id: authData.id,
        email: form.email,
        name: `${form.first_name} ${form.last_name}`,
        worker_id: workerData.id,
      });

      toast.success('Compte créé avec succès !');
      setCurrentStep(3); // Show success step

    } catch (err) {
      console.error('❌ Registration error:', err);
      const message = err instanceof APIError ? err.message : 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    const validationError = validateStep(currentStep, form);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError(null);
    
    if (currentStep === REGISTRATION_STEPS.length - 2) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const renderPersonalInfoStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-teal-50 rounded-xl">
          <UserCircle className="h-6 w-6 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Informations personnelles</h2>
          <p className="text-sm text-gray-500 mt-1">Renseignez vos informations de contact</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormInput
          label="Prénom"
          name="first_name"
          value={form.first_name}
          onChange={(e) => updateForm({ first_name: e.target.value })}
        />
        <FormInput
          label="Nom"
          name="last_name"
          value={form.last_name}
          onChange={(e) => updateForm({ last_name: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={(e) => updateForm({ email: e.target.value })}
          icon={<Mail className="h-5 w-5" />}
        />
        <FormInput
          label="Téléphone"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={(e) => updateForm({ phone: e.target.value })}
          icon={<Phone className="h-5 w-5" />}
          placeholder="+33"
        />
      </div>

      <FormInput
        label="Poste recherché"
        name="job"
        value={form.job}
        onChange={(e) => updateForm({ job: e.target.value })}
        icon={<Briefcase className="h-5 w-5" />}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-6">
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Briefcase className="h-5 w-5 text-gray-400" />
      <label className="text-sm font-medium text-gray-700">
        Types de contrat recherchés
      </label>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {CONTRACT_TYPES.map((option) => (
        <label
          key={option.value}
          className="flex items-center space-x-3 p-3 rounded-lg border-2 border-gray-200 bg-white cursor-pointer hover:border-teal-500 transition-colors"
        >
          <input
            type="checkbox"
            checked={form.contracts.includes(option.value)}
            onChange={() => {
              const updated = form.contracts.includes(option.value)
                ? form.contracts.filter((v) => v !== option.value)
                : [...form.contracts, option.value];
              updateForm({ contracts: updated });
            }}
            className="h-4 w-4 text-teal-500 rounded border-gray-300 focus:ring-teal-500"
          />
          <span className="text-sm text-gray-800">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
</div>

        <FormSelect
          label="Région"
          name="area"
          value={form.area}
          onChange={(e) => updateForm({ area: e.target.value })}
          options={FRENCH_REGIONS}
        />
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PasswordInput
            label="Mot de passe"
            name="password"
            value={form.password}
            onChange={(e) => updateForm({ password: e.target.value })}
            icon={<Lock className="h-5 w-5" />}
            showStrength={true}
          />
          <PasswordInput
            label="Confirmer le mot de passe"
            name="confirm_password"
            value={form.confirm_password}
            onChange={(e) => updateForm({ confirm_password: e.target.value })}
            icon={<Shield className="h-5 w-5" />}
          />
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            checked={form.terms_accepted}
            onChange={(e) => updateForm({ terms_accepted: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 mt-0.5"
          />
          <label className="ml-3 text-sm text-gray-700">
            J'accepte les{' '}
            <a href="#" className="text-teal-600 hover:text-teal-500 underline">
              conditions d'utilisation
            </a>{' '}
            et la{' '}
            <a href="#" className="text-teal-600 hover:text-teal-500 underline">
              politique de confidentialité
            </a>
          </label>
        </div>
      </div>
    </motion.div>
  );

  const renderQualificationsStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-teal-50 rounded-xl">
          <Building2 className="h-6 w-6 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Qualifications</h2>
          <p className="text-sm text-gray-500 mt-1">Ajoutez vos diplômes et certifications</p>
        </div>
      </div>

      <FormInput
        label="Diplôme"
        name="diploma"
        value={form.diploma}
        onChange={(e) => updateForm({ diploma: e.target.value })}
        placeholder="Ex: BAC+3, Master, BTS..."
      />

      <FormSelect
        label="Permis de conduire"
        name="driver_license"
        value={form.driver_license}
        onChange={(e) => updateForm({ driver_license: e.target.value })}
        options={DRIVER_LICENSES}
      />

      <FormInput
        label="CACES"
        name="caces"
        value={form.caces}
        onChange={(e) => updateForm({ caces: e.target.value })}
        required={false}
        placeholder="Exemples: Grues à tour 3, Engins de chantier 5, Grues mobiles 2"
      />

    
    </motion.div>
  );

  const renderDocumentsStep = () => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="space-y-6"
  >
    <div className="flex items-center gap-4 mb-8">
      <div className="p-3 bg-teal-50 rounded-xl">
        <Info className="h-6 w-6 text-teal-600" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
        <p className="text-sm text-gray-500 mt-1">Téléchargez votre CV (optionnel)</p>
      </div>
    </div>

    {/* Bloc enrichi CV */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        CV <span className="text-gray-400">(optionnel)</span>
      </label>
      <label 
        htmlFor="cv-upload" 
        className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-400 hover:bg-teal-50/30 transition-all duration-200 cursor-pointer group"
      >
        <div className="space-y-1 text-center">
          <Info className="mx-auto h-12 w-12 text-gray-400 group-hover:text-teal-500 transition-colors" />
          <div className="flex flex-col items-center text-sm text-gray-600">
            <span className="font-medium text-teal-600 group-hover:text-teal-500 transition-colors">
              {form.cv_classic ? 'Changer le fichier' : 'Cliquez pour téléverser un fichier'}
            </span>
            <p className="text-gray-500">ou glisser-déposer</p>
          </div>
          <p className="text-xs text-gray-500">PDF, DOC, DOCX, PNG, JPG/JPEG, jusqu'à 10MB</p>

          {form.cv_classic && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">Fichier sélectionné</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (form.cv_classic) {
                      const url = URL.createObjectURL(form.cv_classic);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = form.cv_classic.name;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }
                  }}
                  className="text-xs bg-teal-100 text-teal-700 px-3 py-1 rounded-full hover:bg-teal-200 transition-colors"
                >
                  Télécharger
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-2">{form.cv_classic.name}</p>
              <p className="text-xs text-gray-500">
                Taille: {(form.cv_classic.size / 1024 / 1024).toFixed(2)} MB
              </p>

              {form.cv_classic.type.startsWith('image/') && (
                <div className="mt-3">
                  <img
                    src={URL.createObjectURL(form.cv_classic)}
                    alt="CV aperçu"
                    className="mx-auto rounded-md border shadow max-h-32 object-contain"
                    style={{ maxWidth: 200 }}
                  />
                </div>
              )}

              {(form.cv_classic.type === 'application/pdf' ||
                form.cv_classic.type === 'application/msword' ||
                form.cv_classic.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(URL.createObjectURL(form.cv_classic!), '_blank');
                    }}
                    className="text-xs text-teal-700 underline hover:text-teal-800 transition-colors"
                  >
                    Prévisualiser le fichier
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <input
          id="cv-upload"
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          className="sr-only"
          onChange={e => {
            const file = e.target.files?.[0] ?? null;
            setForm(frm => ({
              ...frm,
              cv_classic: file
            }));
          }}
        />
      </label>
    </div>
  </motion.div>
);


  const renderSuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-8"
    >
      <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-teal-100 mb-6">
        <CheckCircle2 className="h-10 w-10 text-teal-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Inscription réussie !</h2>
      <p className="text-gray-600 mb-8">
        Votre profil a été créé avec succès. Vous pouvez maintenant postuler aux offres d'emploi.
      </p>
      <button
        onClick={() => navigate('/profile')}
        className="px-8 py-4 bg-teal-600 text-white rounded-xl font-medium shadow-lg transition transform hover:scale-105 hover:bg-teal-700"
      >
        Accéder à mon profil
      </button>
    </motion.div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderPersonalInfoStep();
      case 1: return renderQualificationsStep();
      case 2: return renderDocumentsStep();
      case 3: return renderSuccessStep();
      default: return renderPersonalInfoStep();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Créez votre profil candidat
          </h1>
          <p className="text-xl text-gray-600">
            Rejoignez notre plateforme et trouvez l'emploi qui vous correspond
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            {REGISTRATION_STEPS.map((step, idx) => (
              <React.Fragment key={step}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex flex-col items-center"
                >
                  <div className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium shadow-lg transition-all',
                    currentStep === idx
                      ? 'bg-teal-600 text-white scale-110 shadow-teal-200'
                      : currentStep > idx
                      ? 'bg-teal-100 text-teal-600'
                      : 'bg-white text-gray-400'
                  )}>
                    {currentStep > idx ? <CheckCircle className="h-6 w-6" /> : idx + 1}
                  </div>
                  <span className={cn(
                    'mt-3 text-sm font-medium transition-colors',
                    currentStep >= idx ? 'text-gray-900' : 'text-gray-400'
                  )}>
                    {step}
                  </span>
                </motion.div>
                {idx < REGISTRATION_STEPS.length - 1 && (
                  <div className={cn(
                    'flex-1 h-1 mx-4 rounded-full transition-colors',
                    currentStep > idx ? 'bg-teal-500' : 'bg-gray-200'
                  )} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <AnimatePresence mode="wait">
            {renderCurrentStep()}
          </AnimatePresence>

          {/* Error Display */}
          {error && currentStep < 3 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
            >
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 3 && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleBack}
                disabled={currentStep === 0}
                className={cn(
                  'flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all',
                  currentStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                )}
              >
                <ChevronLeft className="h-5 w-5" />
                Précédent
              </button>

              <button
                onClick={handleNext}
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-xl font-medium shadow-lg transition-all hover:bg-teal-700 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Création...
                  </>
                ) : currentStep === REGISTRATION_STEPS.length - 2 ? (
                  'Créer mon compte'
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>

       
      </div>
    </div>
  );
}